import {
  Assessment,
  Fullscreen,
  FullscreenExit,
  Link as LinkIcon,
  LocationOn,
  OpenInNew,
  Schedule,
  TrendingUp,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  MaterialReactTable,
  MaterialReactTableProps,
  MRT_ColumnDef,
} from "material-react-table";
import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { EntityTypeKey } from "../../../types/entityRegistry";
import { withDetailRelationship } from "../../../utils/entityAddress";

interface EntityRelationTableProps<T extends Record<string, any>> {
  columns: MRT_ColumnDef<T>[];
  data: T[];
  pageSize?: number;
  enableExpanding?: boolean;
  context?: EntityTypeKey;
  mode?: "overlay" | "standalone";
  // Further options of the table component, laid over the ones set here:
  // for a caller that holds the table's page or marks a row
  tableOptions?: Partial<MaterialReactTableProps<T>>;
  // The least height of the table's box (px or a CSS length); 0 lets a
  // short table end with its last row
  minHeight?: number | string;
  // Draws a hairline round the table, with the corners of a surface
  outlined?: boolean;
}

const EntityRelationTable = <T extends Record<string, any>>({
  columns,
  data,
  pageSize = 10,
  enableExpanding = false,
  context,
  mode = "overlay",
  tableOptions,
  minHeight = 600,
  outlined = false,
}: EntityRelationTableProps<T>) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const handleFullscreenToggle = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleRelationshipClick = (
    relationshipId: string,
    openInNewTab = false
  ) => {
    // Middle-click always opens in new tab (standalone view)
    if (openInNewTab) {
      window.open(`/relation/${relationshipId}`, "_blank");
      return;
    }

    if (mode === "standalone") {
      // In standalone mode, navigate to relationship detail page
      navigate(`/relation/${relationshipId}`);
    } else {
      // In overlay mode, open relationship detail overlay
      setSearchParams(
        // Mutual exclusivity: the entity, id and type, leaves the address
        (prev) => withDetailRelationship(prev, relationshipId),
        { replace: false }
      ); // Create history entry
    }
  };

  const handleEntityClick = (entityId: string) => {
    // Open entity detail view in new tab
    window.open(`/entity/${entityId}`, "_blank");
  };

  // Add action buttons column to the provided columns
  const enhancedColumns = React.useMemo(() => {
    // Check if any row in the data has an entity id
    const hasEntityIds = data.some((row: any) => row.id);
    const hasRelationshipIds = data.some((row: any) => row.relationshipId);

    if (!hasEntityIds && !hasRelationshipIds) {
      return columns; // Return original columns if no IDs found
    }

    const actionColumn: MRT_ColumnDef<T> = {
      accessorKey: "actions" as any,
      header: "",
      size: 80,
      enableSorting: false,
      enableColumnFilter: false,
      enableGlobalFilter: false,
      enableColumnActions: false,
      Cell: ({ row }) => {
        const entityId = (row.original as any).id;
        const relationshipId = (row.original as any).relationshipId;

        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            {/* Relationship button - opens relationship detail in overlay or new tab */}
            {/* Hidden for source context as sources don't have meaningful relationship details */}
            {relationshipId && context !== "source" && (
              <Tooltip
                title={
                  mode === "standalone"
                    ? "Open Relationship (Middle-click for new tab)"
                    : "View Relationship"
                }
              >
                <IconButton
                  size="small"
                  onClick={() => handleRelationshipClick(relationshipId, false)}
                  onMouseDown={(event) => {
                    if (event.button === 1) {
                      event.preventDefault();
                      handleRelationshipClick(relationshipId, true);
                    }
                  }}
                  sx={{
                    color: "text.secondary",
                    "&:hover": { color: "primary.main" },
                  }}
                >
                  <LinkIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {/* Entity button - opens entity detail in new tab */}
            {entityId && (
              <Tooltip title="Open Entity in New Tab">
                <IconButton
                  size="small"
                  onClick={() => handleEntityClick(entityId)}
                  sx={{
                    color: "text.secondary",
                    "&:hover": { color: "primary.main" },
                  }}
                >
                  <OpenInNew fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    };

    // Insert the action column as the last column
    return [...columns, actionColumn];
  }, [columns, data, handleEntityClick, handleRelationshipClick]);

  // Render detail panel for expanded rows
  const renderDetailPanel = ({ row }: { row: any }) => {
    const data = row.original;

    // Detect if this is a source table (sources have specific patterns)
    const isSourceTable =
      data.relationType &&
      [
        "document",
        "news",
        "press-release",
        "website",
        "report",
        "social-media",
      ].includes(data.relationType);

    // Detect if this is a project table (projects have projectSummary or project-specific roles)
    const isProjectTable =
      data.projectSummary ||
      (data.relationType &&
        ["lead", "partner", "contractor", "supplier", "participant"].includes(
          data.relationType
        ));

    return (
      <Box>
        {/* Project Summary (for projects only) - Hide for Source view */}
        {data.projectSummary && context !== "source" && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontWeight: 600 }}
            >
              Project Summary
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                lineHeight: 1.6,
                wordWrap: "break-word",
                overflowWrap: "break-word",
                whiteSpace: "normal",
              }}
            >
              {data.projectSummary}
            </Typography>
          </Box>
        )}

        {/* Relation Summary - Show as "Content Summary" for Source view */}
        {data.relationSummary && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontWeight: 600 }}
            >
              {context === "source"
                ? "Content Summary"
                : "Relationship Summary"}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                lineHeight: 1.6,
                wordWrap: "break-word",
                overflowWrap: "break-word",
                whiteSpace: "normal",
              }}
            >
              {data.relationSummary}
            </Typography>
          </Box>
        )}

        {/* Only show divider if there's a summary AND other content (but not for sources) */}
        {!isSourceTable &&
          (data.projectSummary || data.relationSummary) &&
          (data.investmentAmount ||
            data.ownershipShare ||
            data.relationStart ||
            data.relationEnd ||
            data.confidenceScore ||
            data.count) && <Divider sx={{ my: 2 }} />}

        {/* Relationship Metadata */}
        <Grid container spacing={3}>
          {/* Quality Metrics */}
          {(data.confidenceScore || data.count) && (
            <Grid item xs={12} md={4}>
              <Box>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Assessment fontSize="small" />
                  Quality Metrics
                </Typography>
                {data.confidenceScore && (
                  <Box sx={{ mb: 0.5 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      component="span"
                    >
                      <strong>Confidence:</strong>
                    </Typography>
                    <Chip
                      label={`${(data.confidenceScore * 100).toFixed(0)}%`}
                      size="small"
                      color={
                        data.confidenceScore > 0.8
                          ? "success"
                          : data.confidenceScore > 0.6
                            ? "warning"
                            : "error"
                      }
                      sx={{ ml: 1 }}
                    />
                  </Box>
                )}
                {data.count && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Sources:</strong> {data.count}
                  </Typography>
                )}
              </Box>
            </Grid>
          )}

          {/* Location Details */}
          {!isSourceTable &&
            !isProjectTable &&
            (data.region?.trim() || data.address?.trim()) && (
              <Grid item xs={12} md={4}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <LocationOn fontSize="small" />
                    Location Details
                  </Typography>
                  {data.region?.trim() && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 0.5 }}
                    >
                      <strong>Region:</strong> {data.region}
                    </Typography>
                  )}
                  {data.address?.trim() && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ wordWrap: "break-word", whiteSpace: "normal" }}
                    >
                      <strong>Address:</strong> {data.address}
                    </Typography>
                  )}
                </Box>
              </Grid>
            )}

          {/* Financial Information */}
          {(data.investmentAmount || data.ownershipShare) && (
            <Grid item xs={12} md={4}>
              <Box>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <TrendingUp fontSize="small" />
                  Financial Details
                </Typography>
                {data.investmentAmount && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    <strong>Investment:</strong> {data.investmentAmount}{" "}
                    {data.investmentCurrency || ""}
                  </Typography>
                )}
                {data.ownershipShare && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Ownership:</strong>{" "}
                    {(() => {
                      try {
                        const share = parseFloat(data.ownershipShare);
                        if (share <= 1.0) {
                          return `${(share * 100).toFixed(1)}%`;
                        } else {
                          return `${share.toFixed(1)}%`;
                        }
                      } catch (e) {
                        return String(data.ownershipShare);
                      }
                    })()}
                  </Typography>
                )}
              </Box>
            </Grid>
          )}

          {/* Timeline Information */}
          {!isSourceTable && (data.relationStart || data.relationEnd) && (
            <Grid item xs={12} md={4}>
              <Box>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Schedule fontSize="small" />
                  Timeline
                </Typography>
                {data.relationStart && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    <strong>Start:</strong> {data.relationStart}
                  </Typography>
                )}
                {data.relationEnd && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>End:</strong> {data.relationEnd}
                  </Typography>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  };
  return (
    <Box sx={{ minHeight }}>
      <MaterialReactTable
        columns={enhancedColumns}
        data={data}
        enableColumnFilters
        enableSorting
        enablePagination
        enableTopToolbar={false}
        enableBottomToolbar={true}
        enableGlobalFilter
        enableExpanding={enableExpanding}
        enableColumnActions={true}
        renderDetailPanel={enableExpanding ? renderDetailPanel : undefined}
        getRowCanExpand={() => enableExpanding}
        positionGlobalFilter="left"
        renderBottomToolbarCustomActions={() => (
          <Tooltip
            title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
          >
            <IconButton
              onClick={handleFullscreenToggle}
              size="small"
              sx={{
                color: "text.secondary",
                "&:hover": {
                  color: "primary.main",
                  backgroundColor: "action.hover",
                },
              }}
            >
              {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
            </IconButton>
          </Tooltip>
        )}
        initialState={{
          pagination: { pageSize, pageIndex: 0 },
          density: "compact",
          showGlobalFilter: false,
          showColumnFilters: false,
        }}
        muiTableProps={{
          sx: {
            backgroundColor: "background.paper",
            tableLayout: "fixed", // Force fixed table layout
            width: "100%", // Constrain to container width
            "& .MuiTableHead-root": {
              backgroundColor: "background.paper",
            },
            "& .MuiTableHead-root .MuiTableCell-root": {
              backgroundColor: "background.paper",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
            "& .MuiTableBody-root": {
              backgroundColor: "background.paper",
            },
            "& .MuiTableBody-root .MuiTableCell-root": {
              backgroundColor: "background.paper",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
            "& .MuiTableRow-root": {
              backgroundColor: "background.paper",
            },
            "& .MuiTableRow-root:hover": {
              backgroundColor: "background.paper",
            },
          },
        }}
        muiTablePaperProps={{
          sx: {
            backgroundColor: "background.paper",
            backgroundImage: isFullscreen ? "none" : undefined,
            boxShadow: "none",
            position: isFullscreen ? "absolute" : "relative",
            top: isFullscreen ? 0 : "auto",
            left: isFullscreen ? 0 : "auto",
            right: isFullscreen ? 0 : "auto",
            bottom: isFullscreen ? 0 : "auto",
            zIndex: isFullscreen ? 10 : "auto",
            width: isFullscreen ? "100%" : "auto",
            height: isFullscreen ? "100%" : "auto",
            // The paper's corners come from the theme's surface radius
            ...(outlined && {
              border: 1,
              borderColor: "divider",
              overflow: "hidden",
            }),
            "& .MuiToolbar-root": {
              minHeight: "48px !important",
              backgroundColor: "background.paper",
            },
          },
        }}
        {...tableOptions}
      />
    </Box>
  );
};

export default EntityRelationTable;
